import { useMemo } from 'react'
import { createTheme, useTheme, type Theme } from '@mui/material/styles'

import { COLORS } from '../../../config/constants'
import { calculatorPopperBoundsModifier, calculatorPopperSurfaceStyles } from '../calculator/popups/scaled-popper'

type DemoThemeOverrides = {
  palette?: Record<string, unknown>
  components?: Record<string, unknown>
}

const createDemoTheme = createTheme as unknown as (
  baseTheme: Theme,
  overrides: DemoThemeOverrides,
) => Theme

export const useDemoPlayerThemes = ({ modalContainerRef }: DemoPlayerThemesParams) => {
  const theme = useTheme()

  const calculatorTheme = useMemo(
    () =>
      createDemoTheme(theme, {
        components: {
          MuiDatePicker: {
            defaultProps: {
              desktopModeMediaQuery: '@media (min-width: 0px)',
            },
          },
          MuiPopper: {
            defaultProps: {
              container: () => modalContainerRef.current,
              popperOptions: {
                strategy: 'absolute',
                modifiers: [ calculatorPopperBoundsModifier ],
              },
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    altBoundary: false,
                    altAxis: true,
                    padding: 8,
                  },
                },
                {
                  name: 'flip',
                  options: {
                    altBoundary: false,
                    padding: 8,
                  },
                },
              ],
            },
            styleOverrides: {
              root: {
                ...calculatorPopperSurfaceStyles,
                pointerEvents: 'auto',
              },
            },
          },
          MuiPopover: {
            defaultProps: {
              container: () => modalContainerRef.current,
              marginThreshold: null,
              disableScrollLock: true,
              disableAutoFocus: true,
              disableRestoreFocus: true,
            },
            styleOverrides: {
              root: {
                position: 'absolute',
                pointerEvents: 'auto',
              },
              paper: {
                position: 'absolute',
              },
            },
          },
          MuiDialog: {
            defaultProps: {
              container: () => modalContainerRef.current,
              disableScrollLock: true,
              disableAutoFocus: true,
              disableRestoreFocus: true,
            },
            styleOverrides: {
              root: {
                position: 'absolute',
                pointerEvents: 'auto',
              },
            },
          },
          MuiBackdrop: {
            styleOverrides: {
              root: {
                position: 'absolute',
              },
            },
          },
        },
      }),
    [ theme, modalContainerRef ],
  )

  const introductionTheme = useMemo(
    () =>
      createDemoTheme(theme, {
        palette: {
          primary: {
            main: COLORS.BLUE,
            contrastText: COLORS.WHITE,
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                minHeight: 56,
                '@media (min-width: 768px) and (max-width: 1199px)': {
                  minHeight: 48,
                },
                '@media (min-width: 320px) and (max-width: 767px)': {
                  minHeight: 40,
                },
              },
            },
          },
        },
      }),
    [ theme ],
  )

  const toolbarTheme = useMemo(
    () =>
      createDemoTheme(theme, {
        palette: {
          mode: 'dark',
          error: {
            main: COLORS.RED,
          },
          text: {
            primary: COLORS.WHITE,
            secondary: 'rgba(255,255,255,.7)',
          },
        },
        components: {
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  background: 'transparent',
                  color: COLORS.WHITE,
                  '&:not(.MuiInputBase-multiline)': {
                    height: 40,
                    minHeight: 40,
                  },
                },
                '& .MuiOutlinedInput-input': {
                  height: 24,
                  boxSizing: 'content-box',
                  padding: '8px 12px',
                  color: COLORS.WHITE,
                  background: 'transparent',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,.7)',
                  '&.Mui-focused': {
                    color: COLORS.BLUE,
                  },
                  '&.Mui-error': {
                    color: COLORS.RED,
                  },
                  '&.Mui-focused.Mui-error': {
                    color: COLORS.RED,
                  },
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                color: COLORS.WHITE,
                background: 'transparent',
                fontSize: 14,
                borderRadius: 0,
                '&:not(.MuiInputBase-multiline)': {
                  height: 40,
                  minHeight: 40,
                },
              },
              notchedOutline: {
                borderColor: 'rgba(255,255,255,.23)',
                fontSize: 16,
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: 16,
              },
            },
          },
          MuiInputAdornment: {
            styleOverrides: {
              root: {
                color: COLORS.MEDIUM_GRAY,
              },
            },
          },
        },
      }),
    [ theme ],
  )

  return {
    calculatorTheme,
    introductionTheme,
    toolbarTheme,
  }
}
